/* eslint-disable no-loop-func */
import React, { PureComponent } from "react";
import styled from 'styled-components';
import { Loader } from 'react-loaders';

import { batchRequest, getRequest } from "../../api/ApiService";
import Auth from '../../routes/Auth';
import { clearAppGeneralInfoCache } from '../../api/AppGeneralInfoService';
import { teamsEndpoint } from '../../api/Teams';
import EventEmitter from '../../services/event-emitter';

import TeamsRolesTable from '../components/teams-roles/table';
import TeamsRolesSearch from '../components/teams-roles/search';
import TeamsRolesPagination from '../components/teams-roles/pagination';

import { teamPlayerRowsPerPage } from '../constants';

const Wrapper = styled.div`
  width: 100%;
  padding: 40px 40px 50px;

  .loader {
    display: inline-block;
    margin-top: 20px;
    margin-left: 20px;
  }

  .loader .loader-inner div {
    border-color: #36507e;
  }
`;

const TableWrapper = styled.div`
  border: 1px solid #D5D9DD;
  border-radius: 4px;
  margin-top: 20px;
`;

const PageTitle = styled.h1`
  font-weight: 700;
  font-size: 21px;
  line-height: 28px;
  font-family: 'Mukta Vaani';
`;

class TeamsRoles extends PureComponent {
  constructor(props) {
    super(props);

    this.state = {
      loaded: false,
      teamPlayers: [],
      courseMembers: [],
      teams: [],
      surveys: [],
      surveysFeedbacks: [],
      query: '',
      rowsPerPage: Object.values(teamPlayerRowsPerPage)[0],
      page: 1,
      activePrevPage: null,
      activeNextPage: null
    };
  }

  componentDidMount() {
    this.fetchData();
    this.fetchTeamPlayers();
  }

  fetchTeamPlayers = async () => {
    const { query, rowsPerPage, page } = this.state;

    let teamPlayersAPIUrl = `${process.env.REACT_APP_API_URL}v2/games/teamplayers/?page_size=${rowsPerPage}&page=${page}&game=${Auth.getGameId()}`;

    if (query !== '') {
      teamPlayersAPIUrl += `&search=${query}`;
    }

    getRequest(
      teamPlayersAPIUrl
    )
      .then(res => {
        const teamPlayers = res.body.results;

        const rowsTotal = res.body.count;
        const activePrevPage = res.body.previous;
        const activeNextPage = res.body.next;

        this.setState({
          loaded: true,
          teamPlayers,
          rowsTotal,
          activePrevPage,
          activeNextPage
        });
      })
      .catch(err => { });
  }

  fetchData = () => {
    this.setState({
      loaded: false,
    });

    batchRequest([
      teamsEndpoint(),
      `${process.env.REACT_APP_API_URL}v2/games/coursemembers/`,
      `${process.env.REACT_APP_API_URL}v2/forms/surveys/?course=${Auth.getCourseId()}`,
    ])
      .then(res => {
        let availableTeams = res[0].body.sort((a, b) => a.name.localeCompare(b.name)).reduce((a, v) => ({ ...a, [' ' + v.id]: v.name }), {});

        const courseMembers = res[1].body.results;

        const surveys = res[2].body.results;

        if (surveys && surveys.length > 0) {
          const surveysUrls = surveys.map(survey => `${process.env.REACT_APP_API_URL}v2/forms/feedbacks/?survey=${survey.id}`);
          batchRequest(
            surveysUrls
          )
            .then(async res => {
              let surveysFeedbacks = [];
              for (const [index, response] of res.entries()) {
                surveysFeedbacks[index] = response.body.results;
                const nextPageAvailable = response.body.next;
                if (nextPageAvailable) {
                  const newSurveysFeedbacks = await this.fetchFormsFeedbackPages(nextPageAvailable);
                  surveysFeedbacks[index].push(...newSurveysFeedbacks);
                }
              }

              this.setState({
                loaded: true,
                courseMembers,
                availableTeams,
                surveys,
                surveysFeedbacks
              });
            })
            .catch(err => { });
        } else {
          this.setState({
            loaded: true,
            courseMembers,
            surveys,
            availableTeams
          });
        }
      })
      .catch(err => { });
  };

  fetchFormsFeedbackPages = async (nextPage) => {
    let nextPageAvailable = true;
    let surveysFeedbacks = [];
    let nextPageUrl = nextPage;
    while (nextPageAvailable) {
      nextPageAvailable = false;
      await getRequest(
        nextPageUrl
      )
        .then(res => {
          nextPageUrl = res.body.next;
          nextPageAvailable = res.body.next;
          surveysFeedbacks.push(...res.body.results);
        })
        .catch(err => { });
    }

    return surveysFeedbacks;
  }

  changeTeamPlayers = (teamPlayers) => {
    this.setState({
      teamPlayers
    });
  }

  handleQueryChange = (query) => {
    this.setState(
      {
        query,
        page: 1,
        loaded: false
      },
      () => {
        this.fetchTeamPlayers();
      }
    );
  }

  handleRowsPerPageChange = (rowsPerPage) => {
    this.setState(
      {
        rowsPerPage,
        page: 1,
        loaded: false
      },
      () => {
        this.fetchTeamPlayers();
      }
    );
  }

  handlePageChange = (page) => {
    this.setState(
      {
        page,
        loaded: false
      },
      () => {
        this.fetchTeamPlayers();
      }
    );
  }

  render() {
    const {
      loaded,
      teamPlayers,
      courseMembers,
      availableTeams,
      surveys,
      surveysFeedbacks,
      rowsPerPage,
      rowsTotal,
      page,
      activePrevPage,
      activeNextPage
    } = this.state;

    return (
      <Wrapper>
        <PageTitle>Teams & Roles</PageTitle>
        <TableWrapper>
          <TeamsRolesSearch
            handleQueryChange={this.handleQueryChange}
            game={this.props.game}
            games={this.props.games}
            courseMembers={courseMembers}
            onImportComplete={() => {
              this.fetchData();
              this.fetchTeamPlayers();
              // Invalidate the App-level general-info cache and ask
              // App.js to refetch. Otherwise the Dashboard's Team
              // Count / Student Count KPIs stay stuck on their pre-
              // upload values (they read from App.js's cached
              // general-info state, which doesn't refire on route
              // change) until the user hard-refreshes the page.
              clearAppGeneralInfoCache();
              EventEmitter.getInstance().trigger('reload-data');
            }}
          />
          {
            !loaded &&
            <Wrapper>
              <Loader type="ball-scale-ripple" active />
            </Wrapper>
          }
          {
            loaded &&
            <TeamsRolesTable
              teamPlayers={teamPlayers}
              courseMembers={courseMembers}
              availableTeams={availableTeams}
              surveys={surveys}
              surveysFeedbacks={surveysFeedbacks}
              changeTeamPlayers={this.changeTeamPlayers}
              onRotateComplete={this.fetchTeamPlayers}
            />
          }
          <TeamsRolesPagination
            rowsPerPage={rowsPerPage}
            handleRowsPerPageChange={this.handleRowsPerPageChange}
            rowsTotal={rowsTotal}
            page={page}
            handlePageChange={this.handlePageChange}
            activePrevPage={activePrevPage}
            activeNextPage={activeNextPage}
          />
        </TableWrapper>
      </Wrapper>
    );
  }
}

export default TeamsRoles;



// WEBPACK FOOTER //
// ./src/instructor/containers/teams-roles.js